import { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSaveReservation } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Reservation } from '../../types';
import { toDateInput } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect, DriverSelect } from './EntitySelects';
import { PersonModeToggle, type PersonMode } from './PersonModeToggle';
import { t } from '../../i18n';

interface Values {
  car_id: string;
  date_from: string;
  date_to: string;
  driver_id: string;
  person_name: string;
  purpose: string;
}

/** Pre-filled values, e.g. from a free day clicked in the calendar. */
export interface ReservationDraft {
  car_id?: number;
  date_from?: string;
  date_to?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  reservation?: Reservation | null;
  draft?: ReservationDraft | null;
}

export function ReservationFormModal({ isOpen, onClose, reservation, draft }: Props) {
  const save = useSaveReservation();
  const [personMode, setPersonMode] = useState<PersonMode>('list');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    setPersonMode(reservation && !reservation.driver_id ? 'name' : 'list');
    reset({
      car_id: String(reservation?.car_id ?? draft?.car_id ?? ''),
      date_from: reservation?.date_from ?? draft?.date_from ?? toDateInput(),
      date_to: reservation?.date_to ?? draft?.date_to ?? draft?.date_from ?? toDateInput(),
      driver_id: reservation?.driver_id ? String(reservation.driver_id) : '',
      person_name: reservation?.person_name ?? '',
      purpose: reservation?.purpose ?? '',
    });
  }, [isOpen, reservation, draft, reset]);

  const onSubmit = async (values: Values) => {
    try {
      await save.mutateAsync({
        id: reservation?.id,
        data: {
          car_id: Number(values.car_id),
          date_from: values.date_from,
          date_to: values.date_to,
          driver_id: personMode === 'list' ? Number(values.driver_id) : null,
          person_name: personMode === 'name' ? values.person_name.trim() : null,
          purpose: values.purpose.trim() || null,
        },
      });
      toast.success(reservation ? t('Rezervácia bola uložená') : t('Vozidlo bolo rezervované'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Rezerváciu sa nepodarilo uložiť')));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={save.isPending}
      size="lg"
      title={reservation ? t('Upraviť rezerváciu') : t('Rezervovať vozidlo')}
      description={t('Rezervácia je na celé dni. Vozidlo sa pracovníkovi odovzdá neskôr – so zmluvou alebo interne.')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="reservation-form" loading={save.isPending}>
            {reservation ? t('Uložiť') : t('Rezervovať')}
          </Button>
        </>
      }
    >
      <form id="reservation-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <CarSelect
          label={t('Vozidlo')}
          required
          onlyActive
          wrapperClassName="sm:col-span-2"
          error={errors.car_id?.message}
          {...register('car_id', { required: t('Vyberte vozidlo') })}
        />
        <Input label={t('Od')} type="date" required error={errors.date_from?.message} {...register('date_from', { required: t('Zadajte dátum') })} />
        <Input
          label={t('Do')}
          type="date"
          required
          error={errors.date_to?.message}
          {...register('date_to', {
            required: t('Zadajte dátum'),
            validate: (value, all) => !all.date_from || value >= all.date_from || t('Dátum „do“ nesmie byť skôr ako „od“'),
          })}
        />
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-sm font-medium text-secondary-700">
            {t('Pre koho')} <span className="text-red-500">*</span>
          </p>
          <PersonModeToggle value={personMode} onChange={setPersonMode} />
          {personMode === 'list' ? (
            <DriverSelect
              aria-label={t('Pracovník')}
              placeholder={t('Vyberte zamestnanca alebo živnostníka…')}
              error={errors.driver_id?.message}
              {...register('driver_id', { validate: (v) => personMode !== 'list' || !!v || t('Vyberte pracovníka') })}
            />
          ) : (
            <Input
              aria-label={t('Meno a priezvisko')}
              placeholder={t('Meno a priezvisko')}
              hint={t('Pri odovzdaní vozidla sa osoba pridá do zoznamu vodičov.')}
              error={errors.person_name?.message}
              {...register('person_name', { validate: (v) => personMode !== 'name' || !!v.trim() || t('Zadajte meno') })}
            />
          )}
        </div>
        <Textarea label={t('Účel / dôvod')} rows={2} wrapperClassName="sm:col-span-2" placeholder={t('napr. služobná cesta Viedeň, stavba Žilina…')} {...register('purpose')} />
      </form>
    </Modal>
  );
}
